import React, { useCallback, memo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  selectGeography,
  setTimeBasedAllUSA,
  setTimeBasedInState,
  setTimeBasedOutofState,
  setTimeBasedSpecificStates,
  setTimeBasedZipFile,
  setTimeBasedZip5Codes,
  setTimeBasedZip3Codes,
  setTimeBasedOutsideUS,
} from './GeographySlice'

import { Input } from 'Components/shared/input'
import { useGetZipCodesQuery } from 'Services/configuration/zipCodes'
import { SingleSelectDropdown } from 'Components/shared/singleselectdropdown'
import { FilterSingleSelectDropdown } from 'Components/shared/singleselectdropdown'
import { Permissions } from 'Configs/userPermissions'
import { useHasPermission } from 'Hooks/useHasPermission'
import { VirtualizedMultiSelect } from 'Components/shared/multiselectdropdown'
import { useGetInstitutionsInfoQuery } from 'Services/global/institution'
import { validateZip5Codes, validateZip3Codes } from 'Utils/zipCodes'
import { buildFilterQuery } from 'Filter/buildFilterQuery'
import { FileCategory } from 'Types/fileCategory'
import { Loading } from 'Utils/loading'
import { useLoginStatusQuery } from 'Services/global/authentication'

interface GeographyProps {
  control: any
  filterData?: any
}

export const getFilesfilterQuery = {
  query: {
    filterQuery: {
      properties: [
        {
          column: 'category',
          op: 'eq',
          value: FileCategory.ZipSegmentFilter,
        },
      ],
      operation: 'AND',
    },
  },
}

export const Geography = memo(({ control, filterData }: GeographyProps) => {
  const dispatch = useDispatch()
  const { ipedId } = useParams()
  const geography = useSelector(selectGeography)
  const { data: userData } = useLoginStatusQuery()
  const { hasPermission: canSwitchInstitution } = useHasPermission(Permissions.SwitchInstitution)
  const { data } = useGetInstitutionsInfoQuery(canSwitchInstitution ? undefined : ipedId, {skip: !userData?.isLoggedIn})
  const institution = data?.find((institution) => institution.id === ipedId)
  const { data: zipCodesData, isLoading } = useGetZipCodesQuery({
    ipedId,
    page: 0,
    sortBy: 'updated_at',
    order: 'desc',
    filter: buildFilterQuery(getFilesfilterQuery),
  })
  const [zipFiles, setZipFiles] = useState([])

  const setSelectedValue = useCallback((value: string) => {
    if (value === 'All USA') dispatch(setTimeBasedAllUSA())
    if (value === 'In-State')
      dispatch(setTimeBasedInState(institution?.state_name))
    if (value === 'Out-of-State')
      dispatch(setTimeBasedOutofState(institution?.state_name))
    if (value === 'Specific States/Territories')
      dispatch(setTimeBasedSpecificStates())
    if (value === 'Zipcodes') dispatch(setTimeBasedZipFile())
    if (value === 'Zip Code 5') dispatch(setTimeBasedZip5Codes())
    if (value === 'Zip Code 3') dispatch(setTimeBasedZip3Codes())
    if (value === 'Outside the US') dispatch(setTimeBasedOutsideUS())
  }, [])

  /** This useEffect is for setting filters in edit segment */
  useEffect(() => {
    if (filterData?.[geography.label]) {
      const geographyData = Array.isArray(filterData[geography.label]) ? filterData[geography.label].find((data: any) => data.selectedValue) : filterData[geography.label]
      if (geographyData.selectedValue === 'All USA')
        dispatch(setTimeBasedAllUSA())
      else if (geographyData.selectedValue === 'In-State')
        dispatch(setTimeBasedInState(institution?.state_name))
      else if (geographyData.selectedValue === 'Out-of-State')
        dispatch(setTimeBasedOutofState(institution?.state_name))
      else if (geographyData.selectedValue === 'Specific States/Territories')
        dispatch(setTimeBasedSpecificStates(geographyData.states))
      else if (geographyData.selectedValue === 'Zipcodes')
        dispatch(setTimeBasedZipFile(geographyData.zipCodes))
      else if (geographyData.selectedValue === 'Zip Code 5')
        dispatch(setTimeBasedZip5Codes(geographyData.zipCodes))
      else if (geographyData.selectedValue === 'Zip Code 3')
        dispatch(setTimeBasedZip3Codes(geographyData.zipCodes))
      else if (geographyData.selectedValue === 'Outside the US')
        dispatch(setTimeBasedOutsideUS())
    }
  }, [filterData])

  const setStates = useCallback((value: string[]) => {
    dispatch(setTimeBasedSpecificStates(value))
  }, [])

  useEffect(() => {
    if (zipCodesData) {
      let rows = []
      zipCodesData.records?.forEach((zipCode: any) => {
        rows.push({ id: zipCode.id, name: zipCode.name })
      })
      setZipFiles(rows)
    }
  }, [zipCodesData])

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <FilterSingleSelectDropdown
        label='Geography'
        values={geography.values}
        value={geography.selectedValue}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          setSelectedValue(e.target.value)
        }}
      />
      {geography.selectedValue === 'Specific States/Territories' && (
        <div
          style={{
            marginLeft: '10px',
            marginTop: '10px',
          }}
        >
          <VirtualizedMultiSelect
            label=''
            counterLabel={'States'}
            values={geography.allUSAStatesTerritories}
            initialSelectedValues={geography.selectedStates}
            selectedValues={geography.selectedStates}
            setSelectedValues={setStates}
          />
        </div>
      )}

      {geography.selectedValue === 'Zipcodes' && (
        <div
          style={{
            marginLeft: '10px',
            marginTop: '10px',
          }}
        >
          <SingleSelectDropdown
            id='zipCodes'
            name='zipCodes'
            data-testid='zipCodes'
            control={control}
            rules={{ required: true }}
            style={{ width: '253px' }}
            values={zipFiles?.map((zipFile: any) => zipFile.name)}
            value={geography.zipFile.name}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const zipFile = zipCodesData.records?.find(
                (zipCode: any) => zipCode.name === e.target.value
              )
              dispatch(setTimeBasedZipFile(zipFile))
            }}
          />
        </div>
      )}

      {geography.selectedValue === 'Zip Code 5' && (
        <div
          style={{
            marginLeft: '10px',
            marginTop: '10px',
          }}
        >
          <Input
            id='add-zip5-codes'
            value={geography.zip5Codes}
            name='zip5Codes'
            control={control}
            placeholder='Add Zip Codes separated by commas'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              dispatch(setTimeBasedZip5Codes(e.target.value))
            }}
            rules={{
              validate: validateZip5Codes,
            }}
            multiline
            rows={5}
          />
        </div>
      )}
      {geography.selectedValue === 'Zip Code 3' && (
        <div
          style={{
            marginLeft: '10px',
            marginTop: '10px',
          }}
        >
          <Input
            id='add-zip3-codes'
            value={geography.zip3Codes}
            name='zip3Codes'
            control={control}
            placeholder='Add Zip Codes separated by commas'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              dispatch(setTimeBasedZip3Codes(e.target.value))
            }}
            rules={{
              validate: validateZip3Codes,
            }}
            multiline
            rows={5}
          />
        </div>
      )}
    </>
  )
})
